import { FC, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';

import { Prompt } from '../../../types/prompt';
import { PromptResponse } from '../../../api/prompts';
import HomeContext from '../../../pages/chatv2/Chatv2.context';

interface Props {
  prompt: PromptResponse;
  onClose: () => void;
  onUpdatePrompt: (prompt: PromptResponse) => void;
  isShared: boolean;
}

export const PromptModal: FC<Props> = ({ prompt, onClose, onUpdatePrompt, isShared }) => {
  const [name, setName] = useState(prompt.name);
  const [description, setDescription] = useState(prompt.description);
  const [content, setContent] = useState(prompt.content);

  const {
    state: { loggedOnUser },
  } = useContext(HomeContext);

  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleEnter = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (!isShared || loggedOnUser?.is_admin) {
        onUpdatePrompt({ ...prompt, name, description, content: content.trim() });
      }
      onClose();
    }
  };

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        window.addEventListener('mouseup', handleMouseUp);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      window.removeEventListener('mouseup', handleMouseUp);
      onClose();
    };

    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose]);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      onKeyDown={handleEnter}
    >
      <div className="fixed inset-0 z-10 overflow-hidden">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          />

          <div
            ref={modalRef}
            className="dark:border-netural-400 inline-block max-h-[400px] transform overflow-y-auto rounded-lg border border-gray-300 bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all dark:bg-[#202123] sm:my-8 sm:max-h-[600px] sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
            role="dialog"
          >
            <div className="text-sm font-bold text-black dark:text-neutral-200">
              {'Name'}
            </div>
            <input
              ref={nameInputRef}
              className="mt-2 w-full rounded-lg border border-neutral-500 px-4 py-2 text-neutral-900 shadow focus:outline-none dark:border-neutral-800 dark:border-opacity-50 dark:bg-[#40414F] dark:text-neutral-100"
              placeholder={'A name for your prompt.'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              readOnly={isShared && !loggedOnUser?.is_admin}
            />

            <div className="mt-6 text-sm font-bold text-black dark:text-neutral-200">
              {'Description'}
            </div>
            <textarea
              className="mt-2 w-full rounded-lg border border-neutral-500 px-4 py-2 text-neutral-900 shadow focus:outline-none dark:border-neutral-800 dark:border-opacity-50 dark:bg-[#40414F] dark:text-neutral-100"
              style={{ resize: 'none' }}
              placeholder={'A description for your prompt.'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              readOnly={isShared && !loggedOnUser?.is_admin}
            />

            <div className="mt-6 text-sm font-bold text-black dark:text-neutral-200">
              {'Prompt'}
            </div>
            <textarea
              className="mt-2 w-full rounded-lg border border-neutral-500 px-4 py-2 text-neutral-900 shadow focus:outline-none dark:border-neutral-800 dark:border-opacity-50 dark:bg-[#40414F] dark:text-neutral-100"
              style={{ resize: 'none' }}
              placeholder={'Prompt content. Use {{}} to denote a variable. Ex: {{name}} is a {{adjective}} {{noun}}'}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              readOnly={isShared && !loggedOnUser?.is_admin}
            />

            {(!isShared || loggedOnUser?.is_admin) && (
              <button
                type="button"
                className="w-full px-4 py-2 mt-6 border rounded-lg shadow border-neutral-500 text-neutral-900 hover:bg-neutral-100 focus:outline-none dark:border-neutral-800 dark:border-opacity-50 dark:bg-white dark:text-black dark:hover:bg-neutral-300"
                onClick={() => {
                  const updatedPrompt = {
                    ...prompt,
                    name,
                    description,
                    content: content.trim(),
                  };

                  onUpdatePrompt(updatedPrompt);
                  onClose();
                }}
              >
                {'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
